import { Box, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { Link, Route, Routes, useNavigate } from "react-router-dom";
import MiddleFormCard from "../../components/cards/MiddleFormCard";
import { PasswordInput, TextInput } from "../../components/inputs";
import SubmitButton from "../../components/SubmitButton";
import { useApi } from "../../contexts/ApiContext";
import { useAuth } from "../../contexts/AuthContext";
import { useError } from "../../contexts/ErrorContext";
import { useLogin } from "../../contexts/LoginContext";
import EmailConfirmation from "./EmailConfirmation";
import { SignUpFormData, signUpSchema } from "./types";
import { useApp } from "../../contexts/AppContext";

const Create: React.FC = () => {
  return (
    <Box>
      <Routes>
        <Route element={<EmailConfirmation />} path="/confirm" />
        <Route element={<CreateForm />} path="/" />
      </Routes>
    </Box>
  );
};

const CreateForm: React.FC = () => {
  const [formErrors, setFormErrors] = useState<any>([]);
  const [validateOnChange, setValidateOnChange] = useState(false);

  const { email, password, setEmail, setPassword, setUsername, username } =
    useLogin();

  const navigate = useNavigate();
  const { isLoading, setLoading, setSubmitting } = useApi();

  const { signUp } = useAuth();
  const { translate } = useApp();
  const authTranslations = useMemo(() => translate("auth"), [translate]);

  useEffect(() => {
    if (validateOnChange) {
      validate();
    }
  }, [username, email, password]);

  const validate = () => {
    const formData: SignUpFormData = { username, email, password };
    const result = signUpSchema.safeParse(formData);

    setValidateOnChange(true);
    if (!result.success) {
      const errorDetails = result.error.flatten().fieldErrors;
      setFormErrors(errorDetails as Partial<SignUpFormData>);
      setValidateOnChange(true);
      return false;
    } else {
      setFormErrors({});
    }
    return true;
  };

  const handleSubmit = async () => {
    if (validate()) {
      try {
        setSubmitting(true);
        const response = await signUp(username, password, email);
        const userId = response?.userId;
        if (userId) {
          navigate("/signup/confirm");
        }
      } catch (error) {
        setFormErrors({
          username: authTranslations.errors.username_exists,
        });
      } finally {
        setSubmitting(false);
      }
    }
  };

  return (
    <MiddleFormCard
      onSubmit={handleSubmit}
      title={authTranslations.create.title}
      subtitle={authTranslations.create.subtitle}
    >
      <React.Fragment>
        <TextInput
          label={authTranslations.create.fields.username}
          value={username}
          onChange={(value: number | string) => setUsername(value as string)}
          error={formErrors.username}
          helperText={translate("auth", formErrors.username?.[0])}
        />
        <TextInput
          label={authTranslations.create.fields.email}
          value={email}
          onChange={(value: number | string) => setEmail(value as string)}
          error={formErrors.email}
          helperText={translate("auth", formErrors.email?.[0])}
        />
        <PasswordInput
          label={authTranslations.create.fields.password}
          value={password}
          onChange={(value: string) => setPassword(value)}
          error={formErrors.password}
          helperText={translate("auth", formErrors.password?.[0])}
        />
        <Box mt={2} mb={2} textAlign={"center"}>
          <Typography variant="body2" color="textSecondary">
            {authTranslations.create.privacy_policy}
          </Typography>
        </Box>
        <SubmitButton onClick={handleSubmit}>
          {authTranslations.create.buttons.submit}
        </SubmitButton>
        <Box mt={2} mb={2} textAlign={"center"}>
          <Typography variant="body2" color="textSecondary" mt={2}>
            {authTranslations.create.already_have_account}{" "}
            <Link style={{ fontWeight: "bold" }} to={"/login"}>
              {authTranslations.login.title}
            </Link>
          </Typography>
        </Box>
      </React.Fragment>
    </MiddleFormCard>
  );
};
export default Create;
